<template>
    <div>
        <div class="box view-nav-bar">
            <b-form-input v-model="searchTerm"
                          class="transparent-input"
                          type="search"
                          debounce="400"
                          placeholder="Movie title or Movix ID"/>

            <b-form-input v-model="years"
                          class="transparent-input shx-th-200"
                          type="search"
                          debounce="400"
                          placeholder="Years (e.g. 2024,2025)"/>
        </div>

        <div class="box">
            <div :class="'box-content pt-3' + (results.length ? '' : ' d-none')">
                <b-table id="history-search-table"
                         class="shx-table w-100"
                         hover dark fixed
                         sort-icon-left
                         :busy="fetching"
                         :fields="columns"
                         :items="results"
                         :sort-by.sync="sortBy"
                         :sort-desc.sync="sortDesc">

                    <template #cell(movix_title)="data">
                        <router-link :to="`/history/${data.item.movix_id}`">
                            {{ data.item.movix_title || '—' }}
                        </router-link>
                    </template>

                    <template #cell(released_at)="data">
                        <span>{{ formatDate(data.item.released_at) }}</span>
                    </template>

                    <template #cell(last_admissions_at)="data">
                        <span>{{ formatDate(data.item.last_admissions_at) }}</span>
                    </template>

                    <template #cell(total_admissions_belgium)="data">
                        <span>{{ formatInt(data.item.total_admissions_belgium) }}</span>
                    </template>

                    <template #cell(total_admissions_brightfish)="data">
                        <span>{{ formatInt(data.item.total_admissions_brightfish) }}</span>
                    </template>

                    <template #cell(papayas)="data">
                        <span>{{ formatInt(data.item.papayas) }}</span>
                    </template>

                    <template #table-busy>
                        <table-spinner/>
                    </template>
                </b-table>
            </div>

            <div v-if="!results.length" class="box-content pt-4 text-center text-muted">
                {{ emptyMessage }}
            </div>

            <div v-if="!results.length && !fetching && trimmedTerm" class="box-content pt-3">
                <div v-if="isNumericTerm" class="text-center">
                    <p class="mb-2 text-muted">No history yet for Movix #{{ trimmedTerm }}.</p>
                    <b-button variant="primary"
                              :disabled="compilingId !== null"
                              @click="onCompile(parseInt(trimmedTerm, 10))">
                        <spinner-bars v-if="compilingId !== null"/>
                        <span v-else>Compile from Movix Calendar</span>
                    </b-button>
                </div>

                <div v-else-if="suggestions.length">
                    <heading upper="Found in Movix Calendar" border/>
                    <b-table id="history-suggestions-table"
                             class="shx-table w-100"
                             hover dark fixed
                             :fields="suggestionColumns"
                             :items="suggestions">

                        <template #cell(movix_title)="data">
                            <router-link v-if="data.item.already_compiled"
                                         :to="`/history/${data.item.movix_id}`">
                                {{ data.item.movix_title }}
                            </router-link>
                            <span v-else>{{ data.item.movix_title }}</span>
                        </template>

                        <template #cell(released_at)="data">
                            <span>{{ formatDate(data.item.released_at) }}</span>
                        </template>

                        <template #cell(imdb_id)="data">
                            <span>{{ data.item.imdb_id || '—' }}</span>
                        </template>

                        <template #cell(actions)="data">
                            <router-link v-if="data.item.already_compiled"
                                         :to="`/history/${data.item.movix_id}`"
                                         class="btn btn-sm btn-outline-secondary">
                                View →
                            </router-link>
                            <b-button v-else
                                      size="sm"
                                      variant="primary"
                                      :disabled="compilingId !== null"
                                      @click="onCompile(data.item.movix_id)">
                                <spinner-bars v-if="compilingId === data.item.movix_id"/>
                                <span v-else>Compile</span>
                            </b-button>
                        </template>
                    </b-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { BButton, BFormInput, BTable } from "bootstrap-vue";
import helpers from "../helpers/helpers";
import Heading from "../partials/Heading";
import SpinnerBars from "../partials/SpinnerBars";
import TableSpinner from "../partials/TableSpinner";

export default {
    components: {
        BButton,
        BFormInput,
        BTable,
        Heading,
        SpinnerBars,
        TableSpinner,
    },
    data() {
        const params = helpers.getCurrentUrlQuery();

        this.columns = [
            { key: 'movix_id', label: 'Movix ID', sortable: true, thClass: 'shx-th-100' },
            { key: 'movix_title', label: 'Title', sortable: true },
            { key: 'year', label: 'Year', sortable: true, thClass: 'shx-th-80' },
            { key: 'released_at', label: 'Released', sortable: true, thClass: 'shx-th-120' },
            { key: 'total_admissions_belgium', label: 'BE adm.', sortable: true, thClass: 'text-right shx-th-120', tdClass: 'text-right' },
            { key: 'total_admissions_brightfish', label: 'BF adm.', sortable: true, thClass: 'text-right shx-th-120', tdClass: 'text-right' },
            { key: 'papayas', label: 'Papayas', sortable: true, thClass: 'text-right shx-th-100', tdClass: 'text-right' },
            { key: 'last_admissions_at', label: 'Last adm.', sortable: true, thClass: 'shx-th-120' },
        ];

        this.suggestionColumns = [
            { key: 'movix_id', label: 'Movix ID', thClass: 'shx-th-100' },
            { key: 'movix_title', label: 'Title' },
            { key: 'year', label: 'Year', thClass: 'shx-th-80' },
            { key: 'released_at', label: 'Released', thClass: 'shx-th-120' },
            { key: 'imdb_id', label: 'IMDb', thClass: 'shx-th-120' },
            { key: 'actions', label: '', thClass: 'text-right shx-th-120', tdClass: 'text-right' },
        ];

        // Guards against a slow, older search overwriting a newer one
        this.searchSeq = 0;

        return {
            fetching: false,
            compilingId: null,
            searchTerm: params.term || '',
            years: params.years || '',
            sortBy: params.sortBy || 'total_admissions_belgium',
            sortDesc: params.sort ? params.sort === 'desc' : true,
        };
    },
    computed: {
        results() {
            return this.$store.getters.historyResults;
        },
        suggestions() {
            return this.$store.getters.historySuggestions;
        },
        trimmedTerm() {
            return (this.searchTerm || '').trim();
        },
        isNumericTerm() {
            return /^\d+$/.test(this.trimmedTerm);
        },
        emptyMessage() {
            return this.searchTerm
                ? 'No results.'
                : 'Enter a movie title or Movix ID to start searching.';
        },
    },
    watch: {
        searchTerm() {
            this.runSearch();
        },
        years() {
            this.runSearch();
        },
    },
    created() {
        if (this.searchTerm) {
            this.runSearch();
        }
    },
    methods: {
        runSearch() {
            const seq = ++this.searchSeq;
            const params = { term: this.searchTerm, years: this.years };

            this.fetching = true;
            this.replaceCurrentUri();
            this.$store.dispatch('clearSuggestions');

            this.$store
                .dispatch('searchHistory', params)
                .then(rows => {
                    if (seq !== this.searchSeq || rows.length) {
                        return;
                    }
                    // Movix failures come back as an empty list; anything else is not worth a toast
                    return this.$store.dispatch('fetchSuggestions', params)
                        .then(() => {
                            if (seq !== this.searchSeq) {
                                this.$store.dispatch('clearSuggestions');
                            }
                        })
                        .catch(() => this.$store.dispatch('clearSuggestions'));
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    if (seq === this.searchSeq) {
                        this.fetching = false;
                    }
                });
        },
        onCompile(movixId) {
            this.compilingId = movixId;

            this.$store
                .dispatch('compileHistory', movixId)
                .then(() => {
                    this.$toast.success('History compiled.');
                    this.$router.push(`/history/${movixId}`);
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.compilingId = null;
                });
        },
        replaceCurrentUri() {
            this.$router.replace({
                name: 'HistorySearch',
                query: {
                    ...this.searchTerm && { term: this.searchTerm },
                    ...this.years && { years: this.years },
                    sortBy: this.sortBy,
                    sort: this.sortDesc ? 'desc' : 'asc',
                },
            }).catch(() => { /* swallow NavigationDuplicated */ });
        },
        formatInt(v) {
            return (v || 0).toLocaleString();
        },
        formatDate(v) {
            return v ? this.$dayjs(v).format('YYYY-MM-DD') : '—';
        },
    },
};
</script>
